/**
 * FeedbackButton
 *
 * Floating feedback launcher + modal form. Two fully independent variants:
 *   - mode="parent": professional dashboard styling, star rating.
 *   - mode="kids":   playful kids-mode styling, emoji rating.
 *
 * Both submit to their own backend channel via the feedback service.
 */

import { useState } from 'react';
import { MessageCircleHeart, Star, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { submitParentFeedback, submitKidsFeedback } from '@/services/feedback';

interface FeedbackButtonProps {
  mode: 'parent' | 'kids';
  /** Required when mode="kids" — the child the feedback is tied to. */
  childId?: string;
}

const KIDS_FACES = ['😞', '🙁', '😐', '🙂', '🤩'];

export function FeedbackButton({ mode, childId }: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isKids = mode === 'kids';

  const reset = () => {
    setRating(null);
    setMessage('');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const handleSubmit = async () => {
    if (message.trim().length === 0) {
      toast.error(isKids ? 'Tell Luno a little something first!' : 'Please enter your feedback first.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        message: message.trim(),
        rating,
        page: typeof window !== 'undefined' ? window.location.pathname : null,
      };

      if (isKids) {
        if (!childId) {
          toast.error('Something went wrong. Please try again.');
          return;
        }
        await submitKidsFeedback(childId, payload);
      } else {
        await submitParentFeedback(payload);
      }

      toast.success(isKids ? 'Thank you! Luno got your message! 💛' : 'Thanks for your feedback!');
      handleOpenChange(false);
    } catch {
      toast.error('Could not send your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Give feedback"
        className={
          isKids
            ? 'fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full px-5 py-3 text-white font-bold shadow-lg transition-transform hover:scale-105 active:scale-95'
            : 'fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full px-5 py-3 text-white font-semibold shadow-lg transition-transform hover:-translate-y-0.5'
        }
        style={{ background: isKids ? '#3ECDC6' : '#E8B630' }}
      >
        <MessageCircleHeart className="h-5 w-5" />
        <span className="hidden sm:inline">{isKids ? 'Tell Luno!' : 'Feedback'}</span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className={isKids ? 'max-w-md rounded-3xl' : 'max-w-md'}>
          <DialogHeader>
            <DialogTitle className={isKids ? 'text-2xl' : undefined}>
              {isKids ? 'What do you think? 💬' : 'Share your feedback'}
            </DialogTitle>
            <DialogDescription>
              {isKids
                ? 'Tell Luno what you love or what could be better!'
                : 'We read every message — tell us what’s working and what we can improve.'}
            </DialogDescription>
          </DialogHeader>

          {/* Rating */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-foreground">
              {isKids ? 'How was it?' : 'How would you rate your experience?'}
            </span>
            {isKids ? (
              <div className="flex justify-between gap-1">
                {KIDS_FACES.map((face, i) => {
                  const value = i + 1;
                  const active = rating === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(active ? null : value)}
                      aria-label={`Rating ${value}`}
                      className={`flex-1 rounded-2xl py-2 text-3xl transition-all ${
                        active ? 'scale-110 bg-[#E8F6F4]' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      {face}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => {
                  const active = rating !== null && value <= rating;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(rating === value ? null : value)}
                      aria-label={`${value} star${value > 1 ? 's' : ''}`}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className="h-6 w-6"
                        style={{ color: '#E8B630' }}
                        fill={active ? '#E8B630' : 'none'}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Message */}
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={5}
            placeholder={isKids ? 'Type your message to Luno…' : 'Your feedback…'}
            className={isKids ? 'rounded-2xl text-base' : ''}
          />

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full"
            style={isKids ? { background: '#3ECDC6' } : undefined}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4" />
                {isKids ? 'Send to Luno' : 'Send feedback'}
              </>
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
